import {
	Route,
	Navigate,
	NavigateProps,
	RouterProvider,
	createBrowserRouter,
	createRoutesFromElements,
	useLocation,
	useResolvedPath,
} from 'react-router-dom'
import loadable from '@loadable/component'

import { ScrollToTopLayout } from 'components/common/scroll-to-top'

import {
	AuthLayout,
	MainLayout,
	LandingLayout,
	PayoutLayout,
	EmptyLayout,
	EventLayout,
	EventPublicLayout,
	OfferLayout,
	OfferPublicLayout,
} from 'layouts'
import {
	PublicGuard,
	AuthGuard,
	SocialProcessGuard,
	RecoverGuard,
	SignUp2Guard,
	MfaGuard,
	PayoutProcessGuard,
	InviteGuard,
	VerifyPhoneNumberGuard,
	PasskeyRegistrationGuard,
} from 'guards'
import { useAuth } from 'hooks/useAuth'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { devMode } from 'utils/constants'
import { trimTrailingSlashes } from 'utils/trim-slashes'
import { ComponentClass, ComponentType, Suspense, lazy } from 'react'
import { boolean } from 'boolean'

// Auth pages suspend while their chunk loads, so the auth card never mounts empty and shifts when its form arrives.
// The Suspense around RouterProvider keeps the previous page on screen meanwhile (navigation runs in a transition).
const lazyAuthPage = (load: () => Promise<{ default: ComponentType<any> }>) =>
	lazy(() => lazyRetry(load) as Promise<{ default: ComponentType<any> }>)

// auth pages
const LoginPage = lazyAuthPage(() => import('pages/auth/login'))
const SignUpPage = lazyAuthPage(() => import('pages/auth/sign-up'))
const SignUp2Page = lazyAuthPage(() => import('pages/auth/sign-up-2'))
const ForgotPasswordPage = lazyAuthPage(() => import('pages/auth/forgot-password'))
const ResetPasswordPage = lazyAuthPage(() => import('pages/auth/reset-password'))
const TwoFAVerificationPage = lazyAuthPage(() => import('pages/auth/two-fa-verification'))
const AuthProcessPage = lazyAuthPage(() => import('pages/auth/process'))
const RecoverPage = lazyAuthPage(() => import('pages/auth/recover'))
const ImLoginPage = lazyAuthPage(() => import('pages/auth/im-login'))
const VerifyPhoneNumberPage = lazyAuthPage(() => import('pages/auth/verify-phone-number'))
const RegisterPasskeyPage = lazyAuthPage(() => import('pages/auth/register-passkey'))
const ConfirmEmailPage = lazyAuthPage(() => import('pages/auth/confirm-email'))
const ConfirmEmailChangePage = lazyAuthPage(() => import('pages/auth/confirm-email-change'))

// main pages
const EventsPage = loadable(() => lazyRetry(() => import('pages/Events')))
const SearchPage = loadable(() => lazyRetry(() => import('pages/search')))
const ProfilePage = loadable(() => lazyRetry(() => import('pages/profile')))
const ProfileInformationPage = loadable(() => lazyRetry(() => import('pages/profile/information')))
const ProfileStripePage = loadable(() => lazyRetry(() => import('pages/profile/stripe')))
const SocialPage = loadable(() => lazyRetry(() => import('pages/profile/social')))
const TaxConfigurationPage = loadable(() => lazyRetry(() => import('pages/profile/tax-configuration')))
const NotificationsPage = loadable(() => lazyRetry(() => import('pages/profile/notifications')))
const HostInboxPage = loadable(() => lazyRetry(() => import('pages/host-inbox')))
const InvitesPage = loadable(() => lazyRetry(() => import('pages/profile/invites')))
const TermsOfServicePage = loadable(() => lazyRetry(() => import('pages/terms-of-service')))
const PrivacyPolicyPage = loadable(() => lazyRetry(() => import('pages/privacy-policy')))
const AcceptInvitePage = loadable(() => lazyRetry(() => import('pages/accept-invite')))
const AcceptContactSharePage = loadable(() => lazyRetry(() => import('pages/accept-contact-share')))
const FacebookDataDeletionInstructionsUrlPage = loadable(() =>
	lazyRetry(() => import('pages/facebook-data-deletion-instructions-url'))
)
const UnsubscribePage = loadable(() => lazyRetry(() => import('pages/unsubscribe')))
const CertificatePage = loadable(() => lazyRetry(() => import('pages/certificate')))
const GuestPassPage = loadable(() => lazyRetry(() => import('pages/guest-pass')))
const PartnerConnectPage = loadable(() => lazyRetry(() => import('pages/partners/connect')))
const PartnerDisconnectPage = loadable(() => lazyRetry(() => import('pages/partners/disconnect')))
const UserOffers = loadable(() => lazyRetry(() => import('pages/RepClubs/Offers/UserOffers/UserOffers')))
const LandingPage = loadable(() => lazyRetry(() => import('pages/landing')))

// rep club
const HowToCreateIt = loadable(() => lazyRetry(() => import('pages/RepClubs/HowToCreateIt/HowToCreateIt')))
const RepClubCreateEdit = loadable(() => lazyRetry(() => import('pages/RepClubs/CreateEdit/CreateEdit')))
const MyRepClubDetails = loadable(() => lazyRetry(() => import('pages/RepClubs/MyRepClubDetail/MyRepClubDetail')))
const RepClubDetails = loadable(() => lazyRetry(() => import('pages/RepClubs/RepClubDetail/RepClubDetail')))
const RepClubDetailEvents = loadable(() => lazyRetry(() => import('pages/RepClubs/RepClubDetail/Events')))
const RepClubDetailOffers = loadable(() => lazyRetry(() => import('pages/RepClubs/RepClubDetail/Offers')))
const RepClubHomePage = loadable(() => lazyRetry(() => import('pages/RepClubs')))
const RepClubEventsPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events')))
const RepClubEventsClubPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/club-events')))
const RepClubEventCreatePage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/Create')))
const RepClubEventDetailPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/detail')))
const RepClubEventSeriesDetailPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/series-detail')))
const RepClubEventSeriesWorkspacePage = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Events/series-workspace'))
)
const SeriesOverviewScopePage = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Events/series-workspace/overview'))
)
const SeriesTicketsScopePage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/series-workspace/tickets')))
const SeriesTeamScopePage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/series-workspace/team')))
const SeriesEventScopePage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/series-workspace/event')))
const RepClubEventSeriesRolesPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/series-roles')))
const RepClubEventDetailRevenuePage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/detail/revenue')))
const RepClubEventDetailInboxPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/detail/inbox')))
const RepClubEventRevenuePage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/revenue')))
const RepClubEventQuickLinkPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/quick-link')))
const RepClubEventApprovalPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/Approval')))
const RepClubContactsManagePage = loadable(() => lazyRetry(() => import('pages/RepClubs/Contacts')))
const RepClubContactsInvitePage = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Contacts/invite/event-invite-page'))
)
const RepClubContactsOfferInvitePage = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Contacts/invite/offer-invite-page'))
)
const RepClubEventGuestPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/guest')))
const RepClubEventScanRecordsPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/scan-records')))
const RepClubEventInviteesPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/invitees')))
const RepClubEventLinkOfferPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Events/LinkOffer')))
const RepClubTeamPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Team')))
const RepClubOffersPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Offers')))
const RepClubOffersCreatePage = loadable(() => lazyRetry(() => import('pages/RepClubs/Offers/WayToRewardMembers')))
const RepClubOffersDetails = loadable(() => lazyRetry(() => import('pages/RepClubs/Offers/OfferDetails')))
const RepClubSelectOfferType = loadable(() => lazyRetry(() => import('pages/RepClubs/Offers/SelectOfferType')))
const RepClubSelectOfferSubType = loadable(() => lazyRetry(() => import('pages/RepClubs/Offers/SelectOfferSubType')))
const RepClubAchievementsPage = loadable(() => lazyRetry(() => import('pages/RepClubs/Achievements/Achivements')))
const RepClubAchievementCreatePage = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Achievements/Create/Create'))
)
const RepClubAchievementDetailPage = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Achievements/Detail/Detail'))
)
const RepClubLoyaltyTiersPage = loadable(() => lazyRetry(() => import('pages/RepClubs/LoyaltyTiers/LoyaltyTiers')))
const RepClubAccounts = loadable(() => lazyRetry(() => import('pages/RepClubs/Accounts/Accounts')))
const RepClubEdit = loadable(() => lazyRetry(() => import('pages/RepClubs/EditRepClub/EditRepClub')))

// RepPoints
const RepClubRepPointsPage = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/RepPoints/ManageRepPoints/ManageRepPoints'))
)
const RepClubBuyPointsPage = loadable(() => lazyRetry(() => import('pages/RepClubs/RepPoints/BuyPoints/BuyPoints')))
const RepClubRepPointsTradeRequests = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/RepPoints/TradeRequests/TradeRequests'))
)
const RepClubRepPointsTradeHistory = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/RepPoints/TradeHistory/TradeHistory'))
)
const RepClubRepPointsMyPoints = loadable(() => lazyRetry(() => import('pages/RepClubs/RepPoints/MyPoints/MyPoints')))

// WayToEarnStatus
const RepClubWayToEarnStatusBuyNow = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnStatus/BuyNow/BuyNow'))
)
const RepClubWayToEarnStatusActionAmount = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnStatus/ActionAmount/ActionAmount'))
)
const RepClubWayToEarnStatusInviteOnly = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnStatus/InviteOnly/InviteOnly'))
)
const RepClubWayToEarnStatusLevelUp = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnStatus/LevelUp/LevelUp'))
)
const RepClubWayToEarnStatusPromo = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnStatus/Promo/Promo'))
)
const RepClubWayToEarnStatusStreak = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnStatus/Streak/Streak'))
)
// WayToEarnPoints
const RepClubWayToEarnPointsOnLocation = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnPoints/OnLocation/OnLocation'))
)
const RepClubWayToEarnPointsSocialAction = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnPoints/SocialAction/SocialAction'))
)
const RepClubWayToEarnPointsStreak = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnPoints/Streak/Streak'))
)
const RepClubWayToEarnPointsSurveys = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnPoints/Surveys/Surveys'))
)
const RepClubWayToEarnPointsBonus = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnPoints/Bonus/Bonus'))
)
const RepClubWayToEarnPointsDownload = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnPoints/Download/Download'))
)
const RepClubWayToEarnPointsInteractWithBrandedPost = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnPoints/InteractWithBrandedPost/InteractWithBrandedPost'))
)
const RepClubWayToEarnPointsBuyProduct = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToEarnPoints/BuyProduct/BuyProduct'))
)
// WayToBuyPoints
const RepClubWayToBuyPointsBonusBuy = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToBuyPoints/BonusBuy/BonusBuy'))
)
const RepClubWayToBuyPointsPointsBundle = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToBuyPoints/PointsBundle/PointsBundle'))
)
const RepClubWayToBuyPointsStreak = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToBuyPoints/Streak/Streak'))
)
const RepClubWayToBuyPointsTimeSpan = loadable(() =>
	lazyRetry(() => import('pages/RepClubs/Offers/WayToBuyPoints/TimeSpan/TimeSpan'))
)

const RepClubWayToRewardMembers = loadable(() => lazyRetry(() => import('pages/RepClubs/Offers/WayToRewardMembersV2')))

//event pages
const EventRSVPPage = loadable(() => lazyRetry(() => import('pages/Events/RSVP')))
const EventDetailPublicPage = loadable(() => lazyRetry(() => import('pages/Events/detail')))
const EventSeriesViewAllPage = loadable(() => lazyRetry(() => import('pages/EventSeries/view-all')))
const UserProfilePage = loadable(() => lazyRetry(() => import('pages/User')))

// offer pages
const MemberOfferDetailPage = loadable(() => lazyRetry(() => import('pages/offers/details')))
const MemberOfferCheckoutPage = loadable(() => lazyRetry(() => import('pages/offers/checkout')))
const PublicOfferDetailPage = loadable(() => lazyRetry(() => import('pages/offers/public-details')))
const RedeemOfferPage = loadable(() => lazyRetry(() => import('pages/redeem-offer')))

// payout pages
const PayoutProcessPage = loadable(() => lazyRetry(() => import('pages/Payout/Process')))

// other pages
const NotFoundPage = loadable(() => lazyRetry(() => import('pages/not-found')))

const SettingsPage = loadable(() => lazyRetry(() => import('pages/settings')))

// a function to retry loading a chunk to avoid chunk load error for out of date code
// componentImport and component are typed as any because @loadable/component's dynamic
// import callback does not expose a strongly-typed generic we can use here without
// significant refactoring of every lazy route declaration.
const lazyRetry = function (
	componentImport: any
): Promise<ComponentClass<any, any> | { default: ComponentClass<any, any> }> {
	return new Promise((resolve, reject) => {
		// check if the window has already been refreshed
		const hasRefreshed = JSON.parse(window.sessionStorage.getItem('retry-lazy-refreshed') || 'false')
		// try to import the component
		componentImport()
			.then((component: any) => {
				window.sessionStorage.setItem('retry-lazy-refreshed', 'false') // success so reset the refresh
				resolve(component)
			})
			.catch((error: any) => {
				if (!hasRefreshed) {
					// not been refreshed yet
					window.sessionStorage.setItem('retry-lazy-refreshed', 'true') // we are now going to refresh
					return window.location.reload() // refresh the page
				}
				reject(error) // Default error behaviour as already tried refresh
			})
	})
}

const NavigateWithQuery = ({ to }: Pick<NavigateProps, 'to'>) => {
	const { search } = useLocation()
	return <Navigate to={`${to}${search}`} replace />
}

// The series page: the workspace (header, scope rail and the pane its child route renders) when the
// `seriesWorkspace` flag is on, the old single-page series view otherwise. With the flag off the workspace's
// scope URLs (tickets, team, events/:id) fall back to the series page itself.
const SeriesDetailRoute = () => {
	const workspaceEnabled = useFeatureFlag('seriesWorkspace')
	const { pathname } = useLocation()
	const seriesPagePath = useResolvedPath('.').pathname
	if (workspaceEnabled) {
		return <RepClubEventSeriesWorkspacePage title="Event Series" />
	}
	// A scope URL (tickets, team, events/:id): relative `.` is this route's own path, the series page.
	const onScopeUrl = trimTrailingSlashes(pathname) !== trimTrailingSlashes(seriesPagePath)
	return onScopeUrl ? <Navigate to="." replace /> : <RepClubEventSeriesDetailPage title="Event Series" />
}

// Helper component to handle home redirect based on feature flags
const HomeRedirect = () => {
	const homePage = devMode('Home page & nav item') ? 'home' : 'events'
	return <Navigate to={homePage} replace />
}

const HomeIndex = () => {
	const { token, authInfo } = useAuth()
	const isAuthenticated = Boolean(token) && authInfo && !boolean(authInfo.isDeleted) && boolean(authInfo.isComplete)

	if (isAuthenticated) {
		return <HomeRedirect />
	}

	return <LandingPage />
}

export default function Router() {
	const { token } = useAuth()
	const isDevModeEnabled = Boolean(devMode('Event Quick Link'))
	devMode('Link Offer to Event')
	const router = createBrowserRouter(
		createRoutesFromElements(
			<Route element={<ScrollToTopLayout />}>
				<Route path="auth" element={<AuthLayout />}>
					<Route index element={<Navigate to="login" replace />} />
					<Route
						path="login"
						element={
							<PublicGuard>
								<LoginPage />
							</PublicGuard>
						}
					/>
					<Route
						path="im-login"
						element={
							<PublicGuard>
								<ImLoginPage />
							</PublicGuard>
						}
					/>
					<Route
						path="sign-up"
						element={
							<PublicGuard>
								<SignUpPage />
							</PublicGuard>
						}
					/>
					<Route
						path="sign-up-2"
						element={
							<SignUp2Guard>
								<SignUp2Page />
							</SignUp2Guard>
						}
					/>
					<Route
						path="forgot-password"
						element={
							<PublicGuard>
								<ForgotPasswordPage />
							</PublicGuard>
						}
					/>
					<Route
						path="reset-password"
						element={
							<PublicGuard>
								<ResetPasswordPage />
							</PublicGuard>
						}
					/>
					<Route
						path="confirm-email"
						element={
							<PublicGuard>
								<ConfirmEmailPage />
							</PublicGuard>
						}
					/>
					<Route path="confirm-email-change" element={<ConfirmEmailChangePage />} />
					<Route
						path="mfa"
						element={
							<MfaGuard>
								<TwoFAVerificationPage />
							</MfaGuard>
						}
					/>
					<Route
						path="process"
						element={
							<SocialProcessGuard>
								<AuthProcessPage />
							</SocialProcessGuard>
						}
					/>
					<Route
						path="recover"
						element={
							<RecoverGuard>
								<RecoverPage />
							</RecoverGuard>
						}
					/>
					<Route
						path="verify-phone-number"
						element={
							<VerifyPhoneNumberGuard>
								<VerifyPhoneNumberPage />
							</VerifyPhoneNumberGuard>
						}
					/>
					<Route
						path="register-passkey"
						element={
							<PasskeyRegistrationGuard>
								<RegisterPasskeyPage />
							</PasskeyRegistrationGuard>
						}
					/>
				</Route>
				<Route path="payout" element={<PayoutLayout />}>
					<Route
						path="process"
						element={
							<PayoutProcessGuard>
								<PayoutProcessPage />
							</PayoutProcessGuard>
						}
					/>
				</Route>
				<Route path="terms-of-service" element={<LandingLayout />}>
					<Route index element={<TermsOfServicePage />} />
				</Route>
				<Route path="privacy-policy" element={<LandingLayout />}>
					<Route index element={<PrivacyPolicyPage />} />
				</Route>
				<Route path="facebook-data-deletion-instructions-url" element={<LandingLayout />}>
					<Route index element={<FacebookDataDeletionInstructionsUrlPage />} />
				</Route>
				<Route path="unsubscribe" element={<LandingLayout />}>
					<Route index element={<UnsubscribePage />} />
				</Route>
				<Route path="certificate/:referenceId" element={<EmptyLayout />}>
					<Route index element={<CertificatePage />} />
				</Route>
				<Route path="guest-pass/:eventId/:token" element={<EmptyLayout />}>
					<Route index element={<GuestPassPage />} />
				</Route>
				<Route path="partners/connect" element={<EmptyLayout />}>
					<Route index element={<PartnerConnectPage />} />
				</Route>
				<Route path="partners/disconnect" element={<EmptyLayout />}>
					<Route index element={<PartnerDisconnectPage />} />
				</Route>
				<Route
					path="tabs/profile/confirm-email-change"
					element={<NavigateWithQuery to="/auth/confirm-email-change" />}
				/>
				<Route path="redeem-offer/:token" element={<EmptyLayout />}>
					<Route
						index
						element={
							<AuthGuard>
								<RedeemOfferPage />
							</AuthGuard>
						}
					/>
				</Route>
				<Route
					path="events/:id"
					element={<AuthGuard>{token ? <EventLayout /> : <EventPublicLayout />}</AuthGuard>}
				>
					<Route
						index
						element={
							token ? (
								<EventRSVPPage title="Event detail" />
							) : (
								<EventDetailPublicPage title="Event detail" />
							)
						}
					/>
				</Route>
				<Route
					path="event-series/:slug"
					element={<AuthGuard>{token ? <EventLayout /> : <EventPublicLayout />}</AuthGuard>}
				>
					<Route index element={<EventSeriesViewAllPage title="Event Series" />} />
				</Route>
				<Route
					path="rep-clubs/:club_id/offers/:offer_id/checkout"
					element={<AuthGuard>{token ? <OfferLayout /> : <OfferPublicLayout />}</AuthGuard>}
				>
					<Route index element={<MemberOfferCheckoutPage title="Checkout" />} />
				</Route>
				<Route
					path="rep-clubs/:club_id/offers/:offer_id"
					element={<AuthGuard>{token ? <MainLayout /> : <OfferPublicLayout />}</AuthGuard>}
				>
					<Route
						index
						element={
							token ? (
								<MemberOfferDetailPage title="Offer Detail" />
							) : (
								<PublicOfferDetailPage title="Offer Detail" />
							)
						}
					/>
				</Route>
				<Route
					path="manage/rep-clubs/:club_id?/events"
					element={
						<AuthGuard>
							<EventLayout />
						</AuthGuard>
					}
				>
					<Route path="create" element={<RepClubEventCreatePage title="Create event" />} />
					<Route path=":event_id">
						<Route path="edit" element={<RepClubEventCreatePage title="Edit event" />} />
						{isDevModeEnabled && (
							<Route path="link-offer" element={<RepClubEventLinkOfferPage title="Link Offer" />} />
						)}
					</Route>
				</Route>
				<Route
					path="rep-club/:club_id?/events"
					element={
						<AuthGuard>
							<EventLayout />
						</AuthGuard>
					}
				>
					<Route path="create" element={<RepClubEventCreatePage title="Create event" />} />
					<Route path=":event_id">
						<Route path="edit" element={<RepClubEventCreatePage title="Edit event" />} />
						{isDevModeEnabled && (
							<Route path="link-offer" element={<RepClubEventLinkOfferPage title="Link Offer" />} />
						)}
					</Route>
				</Route>
				<Route path="/">
					<Route index element={<HomeIndex />} />
					<Route
						element={
							<AuthGuard>
								<MainLayout />
							</AuthGuard>
						}
					>
						{isDevModeEnabled && <Route path="home" element={<RepClubHomePage title="Home" />} />}
						<Route path="events" element={<EventsPage title="Events" />} />
						<Route path="search" element={<SearchPage title="Search results" />} />
						<Route path="users/:userId" element={<UserProfilePage />} />
						{/* Rep Clubs Routes - User/Member views only */}
						{isDevModeEnabled && (
							<Route path="rep-clubs/:club_id">
								<Route index element={<RepClubDetails />} />
								<Route path="events" element={<RepClubDetailEvents />} />
								<Route path="offers" element={<RepClubDetailOffers />} />
								<Route path="buy-points" element={<RepClubBuyPointsPage />} />
								<Route
									path="achievement/:achievement_id"
									element={<RepClubAchievementDetailPage title="Achievement" />}
								/>
							</Route>
						)}
						{/* Rep-club routes (parallel to manage/rep-clubs for events) */}
						<Route path="rep-club">
							<Route path="events">
								<Route index element={<RepClubEventsPage title="Manage Events" />} />
								<Route path="revenue" element={<RepClubEventRevenuePage title="Event Revenue" />} />
								{isDevModeEnabled && (
									<Route
										path="quick-link"
										element={<RepClubEventQuickLinkPage title="Event Quick Link" />}
									/>
								)}
								<Route path=":event_id">
									<Route index element={<RepClubEventDetailPage title="Event" />} />
									<Route path="approval" element={<RepClubEventApprovalPage title="Event" />} />
									<Route
										path="invite"
										element={<RepClubContactsInvitePage title="Invite Contacts" />}
									/>
									<Route path="invitees" element={<RepClubEventInviteesPage title="Event" />} />
									<Route path="guest" element={<RepClubEventGuestPage title="Event" />} />
									<Route
										path="scan-records"
										element={<RepClubEventScanRecordsPage title="Scan Records" />}
									/>
									<Route
										path="revenue"
										element={<RepClubEventDetailRevenuePage title="Event Revenue" />}
									/>
									<Route path="inbox" element={<RepClubEventDetailInboxPage title="Event Inbox" />} />
									<Route
										path="messaging-schedule"
										element={<Navigate to="../inbox?tab=pending" replace />}
									/>
								</Route>
							</Route>
							<Route path=":club_id/events">
								<Route index element={<RepClubEventsClubPage title="Manage Events" />} />
								<Route path="revenue" element={<RepClubEventRevenuePage title="Event Revenue" />} />
								{isDevModeEnabled && (
									<Route
										path="quick-link"
										element={<RepClubEventQuickLinkPage title="Event Quick Link" />}
									/>
								)}
								<Route path=":event_id">
									<Route index element={<RepClubEventDetailPage title="Event" />} />
									<Route path="approval" element={<RepClubEventApprovalPage title="Event" />} />
									<Route
										path="invite"
										element={<RepClubContactsInvitePage title="Invite Contacts" />}
									/>
									<Route path="invitees" element={<RepClubEventInviteesPage title="Event" />} />
									<Route path="guest" element={<RepClubEventGuestPage title="Event" />} />
									<Route
										path="scan-records"
										element={<RepClubEventScanRecordsPage title="Scan Records" />}
									/>
									<Route
										path="revenue"
										element={<RepClubEventDetailRevenuePage title="Event Revenue" />}
									/>
									<Route path="inbox" element={<RepClubEventDetailInboxPage title="Event Inbox" />} />
									<Route
										path="messaging-schedule"
										element={<Navigate to="../inbox?tab=pending" replace />}
									/>
								</Route>
							</Route>
						</Route>
						{/* Management Routes - under /manage/rep-clubs/:club_id */}
						<Route path="manage/rep-clubs">
							{isDevModeEnabled && (
								<>
									<Route index element={<HowToCreateIt />} />
									<Route
										path="create"
										element={
											<AuthGuard>
												<RepClubCreateEdit />
											</AuthGuard>
										}
									/>
								</>
							)}
							{/* Contacts management */}
							<Route path="contacts" element={<RepClubContactsManagePage title="Contacts" />} />
							{/* Inbox management */}
							<Route path="inbox" element={<HostInboxPage title="Inbox" />} />

							{/* Events management - non-club specific */}
							<Route path="events">
								<Route index element={<RepClubEventsPage title="Manage Events" />} />
								<Route path="series/:series_id" element={<SeriesDetailRoute />}>
									<Route index element={<SeriesOverviewScopePage />} />
									<Route path="tickets" element={<SeriesTicketsScopePage />} />
									<Route path="team" element={<SeriesTeamScopePage />} />
									<Route path="events/:event_id" element={<SeriesEventScopePage />} />
								</Route>
								<Route
									path="series/:series_id/roles"
									element={<RepClubEventSeriesRolesPage title="Manage Series Roles" />}
								/>
								<Route path="revenue" element={<RepClubEventRevenuePage title="Event Revenue" />} />
								<Route
									path="revenue/stripe"
									element={<ProfileStripePage title="Stripe Account Management" />}
								/>
								{isDevModeEnabled && (
									<Route
										path="quick-link"
										element={<RepClubEventQuickLinkPage title="Event Quick Link" />}
									/>
								)}
								<Route path=":event_id">
									<Route index element={<RepClubEventDetailPage title="Event" />} />
									<Route path="approval" element={<RepClubEventApprovalPage title="Event" />} />
									<Route
										path="invite"
										element={<RepClubContactsInvitePage title="Invite Contacts" />}
									/>
									<Route path="invitees" element={<RepClubEventInviteesPage title="Event" />} />
									<Route path="guest" element={<RepClubEventGuestPage title="Event" />} />
									<Route
										path="scan-records"
										element={<RepClubEventScanRecordsPage title="Scan Records" />}
									/>
									<Route
										path="revenue"
										element={<RepClubEventDetailRevenuePage title="Event Revenue" />}
									/>
									<Route path="inbox" element={<RepClubEventDetailInboxPage title="Event Inbox" />} />
									<Route
										path="messaging-schedule"
										element={<Navigate to="../inbox?tab=pending" replace />}
									/>
								</Route>
							</Route>
							<Route path=":club_id">
								{isDevModeEnabled && (
									<>
										<Route index element={<MyRepClubDetails />} />
										<Route path="edit" element={<RepClubEdit title="Edit Club" />} />
										<Route path="team" element={<RepClubTeamPage title="Manage Team" />} />
										<Route path="accounts" element={<RepClubAccounts title="Accounts" />} />
										<Route
											path="rep-points"
											element={<RepClubRepPointsPage title="Manage Rep Points" />}
										/>
										<Route
											path="loyalty-tiers"
											element={<RepClubLoyaltyTiersPage title="Manage Loyalty Tiers" />}
										/>
									</>
								)}
								{/* Events management - club specific */}
								<Route path="events">
									<Route index element={<RepClubEventsClubPage title="Manage Events" />} />

									<Route path="revenue" element={<RepClubEventRevenuePage title="Event Revenue" />} />
									{isDevModeEnabled && (
										<Route
											path="quick-link"
											element={<RepClubEventQuickLinkPage title="Event Quick Link" />}
										/>
									)}
									<Route path=":event_id">
										<Route index element={<RepClubEventDetailPage title="Event" />} />
										<Route path="approval" element={<RepClubEventApprovalPage title="Event" />} />
										<Route
											path="invite"
											element={<RepClubContactsInvitePage title="Invite Contacts" />}
										/>
										<Route path="guest" element={<RepClubEventGuestPage title="Event" />} />
										<Route
											path="scan-records"
											element={<RepClubEventScanRecordsPage title="Scan Records" />}
										/>
										<Route
											path="revenue"
											element={<RepClubEventDetailRevenuePage title="Event Revenue" />}
										/>
										<Route
											path="inbox"
											element={<RepClubEventDetailInboxPage title="Event Inbox" />}
										/>
										<Route
											path="messaging-schedule"
											element={<Navigate to="../inbox?tab=pending" replace />}
										/>
									</Route>
								</Route>

								{/* Offers management */}
								{isDevModeEnabled && (
									<Route path="offers">
										<Route index element={<RepClubOffersPage title="Manage Offers" />} />
										<Route path="create">
											<Route index element={<RepClubOffersCreatePage title="Add Offer" />} />
											<Route
												path="select-offer-type"
												element={<RepClubSelectOfferType title="Select Offer Type" />}
											/>
											<Route
												path=":offer_type"
												element={<RepClubSelectOfferSubType title="Select Offer Sub Type" />}
											/>

											<Route
												path="way-to-reward-members/:sub_type"
												element={<RepClubWayToRewardMembers />}
											/>
											<Route
												path="way-to-reward-members/:sub_type/:id"
												element={<RepClubWayToRewardMembers />}
											/>

											<Route
												path="way-to-earn-status/action-amount"
												element={<RepClubWayToEarnStatusActionAmount />}
											/>
											<Route
												path="way-to-earn-status/buy-now"
												element={<RepClubWayToEarnStatusBuyNow />}
											/>
											<Route
												path="way-to-earn-status/invite-only"
												element={<RepClubWayToEarnStatusInviteOnly />}
											/>
											<Route
												path="way-to-earn-status/level-up"
												element={<RepClubWayToEarnStatusLevelUp />}
											/>
											<Route
												path="way-to-earn-status/promo"
												element={<RepClubWayToEarnStatusPromo />}
											/>
											<Route
												path="way-to-earn-status/streak"
												element={<RepClubWayToEarnStatusStreak />}
											/>

											<Route
												path="way-to-earn-points/streak"
												element={<RepClubWayToEarnPointsStreak />}
											/>
											<Route
												path="way-to-earn-points/social-action"
												element={<RepClubWayToEarnPointsSocialAction />}
											/>
											<Route
												path="way-to-earn-points/location"
												element={<RepClubWayToEarnPointsOnLocation />}
											/>
											<Route
												path="way-to-earn-points/survey"
												element={<RepClubWayToEarnPointsSurveys />}
											/>
											<Route
												path="way-to-earn-points/bonus"
												element={<RepClubWayToEarnPointsBonus />}
											/>
											<Route
												path="way-to-earn-points/download"
												element={<RepClubWayToEarnPointsDownload />}
											/>
											<Route
												path="way-to-earn-points/interact"
												element={<RepClubWayToEarnPointsInteractWithBrandedPost />}
											/>
											<Route
												path="way-to-earn-points/buy-product"
												element={<RepClubWayToEarnPointsBuyProduct />}
											/>

											<Route
												path="way-to-buy-points/points-bundle"
												element={<RepClubWayToBuyPointsPointsBundle />}
											/>
											<Route
												path="way-to-buy-points/bonus-buys"
												element={<RepClubWayToBuyPointsBonusBuy />}
											/>
											<Route
												path="way-to-buy-points/time-span"
												element={<RepClubWayToBuyPointsTimeSpan />}
											/>
											<Route
												path="way-to-buy-points/streak"
												element={<RepClubWayToBuyPointsStreak />}
											/>
										</Route>
										<Route path=":offer_id">
											<Route index element={<RepClubOffersDetails title="Offer Detail" />} />
											<Route
												path="invite"
												element={<RepClubContactsOfferInvitePage title="Offer Invite" />}
											/>
											<Route path="edit">
												<Route
													path="select-offer-type"
													element={<RepClubSelectOfferType title="Select Offer Type" />}
												/>
												<Route
													path=":offer_type"
													element={
														<RepClubSelectOfferSubType title="Select Offer Sub Type" />
													}
												/>
												<Route
													path="way-to-reward-members/:sub_type"
													element={<RepClubWayToRewardMembers />}
												/>
												<Route
													path="way-to-reward-members/:sub_type/:id"
													element={<RepClubWayToRewardMembers />}
												/>

												<Route
													path="way-to-earn-status/action-amount"
													element={<RepClubWayToEarnStatusActionAmount />}
												/>
												<Route
													path="way-to-earn-status/buy-now"
													element={<RepClubWayToEarnStatusBuyNow />}
												/>
												<Route
													path="way-to-earn-status/invite-only"
													element={<RepClubWayToEarnStatusInviteOnly />}
												/>
												<Route
													path="way-to-earn-status/level-up"
													element={<RepClubWayToEarnStatusLevelUp />}
												/>
												<Route
													path="way-to-earn-status/promo"
													element={<RepClubWayToEarnStatusPromo />}
												/>
												<Route
													path="way-to-earn-status/streak"
													element={<RepClubWayToEarnStatusStreak />}
												/>

												<Route
													path="way-to-earn-points/streak"
													element={<RepClubWayToEarnPointsStreak />}
												/>
												<Route
													path="way-to-earn-points/social-action"
													element={<RepClubWayToEarnPointsSocialAction />}
												/>
												<Route
													path="way-to-earn-points/location"
													element={<RepClubWayToEarnPointsOnLocation />}
												/>
												<Route
													path="way-to-earn-points/survey"
													element={<RepClubWayToEarnPointsSurveys />}
												/>
												<Route
													path="way-to-earn-points/bonus"
													element={<RepClubWayToEarnPointsBonus />}
												/>
												<Route
													path="way-to-earn-points/download"
													element={<RepClubWayToEarnPointsDownload />}
												/>
												<Route
													path="way-to-earn-points/interact"
													element={<RepClubWayToEarnPointsInteractWithBrandedPost />}
												/>
												<Route
													path="way-to-earn-points/buy-product"
													element={<RepClubWayToEarnPointsBuyProduct />}
												/>

												<Route
													path="way-to-buy-points/points-bundle"
													element={<RepClubWayToBuyPointsPointsBundle />}
												/>
												<Route
													path="way-to-buy-points/bonus-buys"
													element={<RepClubWayToBuyPointsBonusBuy />}
												/>
												<Route
													path="way-to-buy-points/time-span"
													element={<RepClubWayToBuyPointsTimeSpan />}
												/>
												<Route
													path="way-to-buy-points/streak"
													element={<RepClubWayToBuyPointsStreak />}
												/>
											</Route>
										</Route>
									</Route>
								)}
								{/* Achievements management */}
								{isDevModeEnabled && (
									<Route path="achievements">
										<Route index element={<RepClubAchievementsPage title="Achievements" />} />
										<Route
											path="create"
											element={<RepClubAchievementCreatePage title="Add Achievement" />}
										/>
										<Route
											path=":achievement_id"
											element={<RepClubAchievementDetailPage title="Achievement" />}
										/>
										<Route
											path=":achievement_id/edit"
											element={<RepClubAchievementCreatePage title="Edit Achievement" />}
										/>
									</Route>
								)}
							</Route>
						</Route>
						{isDevModeEnabled && (
							<Route path="rep-points">
								<Route path="my-points" element={<RepClubRepPointsMyPoints title="My rep points" />} />
								<Route
									path="trade-history"
									element={<RepClubRepPointsTradeHistory title="Rep points trade history" />}
								/>
								<Route
									path="trade-requests"
									element={<RepClubRepPointsTradeRequests title="Rep points trade requests" />}
								/>
							</Route>
						)}
						<Route path="profile">
							<Route index element={<ProfilePage title="Profile" />} />
							<Route
								path="information"
								element={<ProfileInformationPage title="Profile" />}
							/>
							<Route path="personal" element={<Navigate to="/profile/information" replace />} />
							<Route path="social" element={<SocialPage title="Social accounts" />} />
							<Route path="location" element={<Navigate to="/profile/information" replace />} />
							<Route
								path="tax-configuration"
								element={<TaxConfigurationPage title="Tax Configuration" />}
							/>
							<Route path="notifications" element={<NotificationsPage title="Notifications" />} />
							<Route path="invites" element={<InvitesPage title="Club invites" />} />
							<Route path="settings" element={<SettingsPage title="Settings" />} />
						</Route>
						{/* User offers page */}
						{isDevModeEnabled && <Route path="offers/:tab" element={<UserOffers title="Offers" />} />}
					</Route>
				</Route>
				<Route
					path="accept-invite/:id"
					element={
						<InviteGuard>
							<EmptyLayout />
						</InviteGuard>
					}
				>
					<Route index element={<AcceptInvitePage />} />
				</Route>
				<Route
					path="accept-contact-share/:inviteToken"
					element={
						<InviteGuard>
							<EmptyLayout />
						</InviteGuard>
					}
				>
					<Route index element={<AcceptContactSharePage />} />
				</Route>
				<Route path="*" element={<NotFoundPage />} />
			</Route>
		),
		{
			future: {
				v7_relativeSplatPath: true,
			},
		}
	)

	return (
		<Suspense fallback={null}>
			<RouterProvider router={router} future={{ v7_startTransition: true }} />
		</Suspense>
	)
}
